import { MutableRefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { onPaste, allowNumbersOnly } from '@/utils/inputHelper';
import type { DateRange } from 'react-day-picker';
import type { Filter } from '@/models/Filter';
import useMap from '@/hooks/useMap';
import SearchBar, { type SearchBarFilter } from '@/components/search-bar/SearchBar';
import type { Locale } from '@/i18n';
import useDateFnsLocale from '@/hooks/useDateFnsLocale';
import { useApplyFilter } from '@/hooks/useApplyFilter';
import type { AutocompleteData } from '@/utils/autocompleteHelper';
import { MAX_GUEST_ROOMS_LIMIT } from '@/constants/filterConstants';
import useAnalytics from '@/hooks/useAnalytics';
import { getSearchBarFact } from '@/analytics/helpers/cventAnalytics/getSearchBarFact';
import { useSearchParams } from 'next/navigation';
import { refreshSearchCookie } from '@/utils/cookieHelper';
import type { SearchBarFact } from '@/analytics/models/cventAnalytics/SearchBarFact';
import useGeocoder from '@/hooks/useGeocoder';
import { DECADE_COOKIE_TTL } from '@/constants/cookieConstants';
import { queryStringConstants } from '@/constants/queryConstants';
import { isFullMapView } from '@/utils/maps/mapUtils';
import { validateAttendees } from '@/utils/filters/filterHelper';

export type InputRefs = {
  searchTermInputRef: MutableRefObject<HTMLInputElement | null>;
  startInputRef: MutableRefObject<HTMLDivElement | null>;
  endInputRef: MutableRefObject<HTMLDivElement | null>;
  attendeesInputRef: MutableRefObject<HTMLInputElement | null>;
  guestRoomsInputRef: MutableRefObject<HTMLInputElement | null>;
};

type Props = {
  searchTerm: string;
  labels: Record<string, string>;
  countryCode: string | undefined;
  locale: Locale;
  minLength?: number;
  lat?: number;
  lon?: number;
  radius?: number;
  cookieDomain: string;
  filters: Filter;
  setFilters: (f: Filter) => void;
  onAttendeesChange: (n: React.ChangeEvent<HTMLInputElement>, applyFilter?: boolean) => Promise<void>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onRoomsChange: (n: any, isSetFromPopper?: boolean, applyFilter?: boolean) => Promise<void>;
  handleRangeSelect: (r: DateRange) => void;
  eventStart: string | undefined;
  eventEnd: string | undefined;
  checkInDate: Date | undefined;
  checkOutDate: Date | undefined;
  setCheckInDate: (d: Date | undefined) => void;
  setCheckOutDate: (d: Date | undefined) => void;
  setIsSearchExpanded: (b: boolean) => void;
  inputRefs: InputRefs;
  isDatePickerOpen: boolean;
  setIsDatePickerOpen: (b: boolean) => void;
  setIsAutocompleteOpen: (b: boolean) => void;
  isGuestRoomDatePickerOpen: boolean;
  setIsGuestRoomDatePickerOpen: (b: boolean) => void;
};

const COUNTRY_OVERRIDE_COOKIE = 'sriracha-country-override';

export default function SearchBarContainer({
  filters,
  setFilters,
  onAttendeesChange,
  onRoomsChange,
  handleRangeSelect,
  eventStart,
  eventEnd,
  checkInDate,
  checkOutDate,
  setCheckInDate,
  setCheckOutDate,
  searchTerm,
  labels,
  countryCode,
  locale,
  minLength = 3,
  lat,
  lon,
  radius,
  cookieDomain,
  setIsSearchExpanded,
  inputRefs,
  setIsAutocompleteOpen,
  isDatePickerOpen,
  setIsDatePickerOpen,
  isGuestRoomDatePickerOpen,
  setIsGuestRoomDatePickerOpen
}: Props) {
  const dateFnsLocale = useDateFnsLocale(locale);

  const [userModifiedGRDate, setUserModifiedGRDate] = useState<boolean>(false);

  const [term, setTerm] = useState(searchTerm);
  const [autosuggestion, setAutoSuggestion] = useState<AutocompleteData>(null);
  const [isValidSearchTerm, setIsValidSearchTerm] = useState(true);

  const onGuestRoomDateChange = useCallback(
    (from: Date | undefined, to: Date | undefined) => {
      setCheckInDate(from);
      setCheckOutDate(to);
    },
    [setCheckInDate, setCheckOutDate]
  );

  useEffect(() => {
    if (!userModifiedGRDate) {
      if (!filters.GUEST_CHECKIN_DATE) {
        setCheckInDate(filters.EVENT_START_DATE);
      }
      if (!filters.GUEST_CHECKOUT_DATE) {
        setCheckOutDate(filters.EVENT_END_DATE);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userModifiedGRDate, filters.EVENT_START_DATE, filters.EVENT_END_DATE]);

  const { applyFilters } = useApplyFilter();
  const {
    search,
    startDate,
    endDate,
    attendees,
    guestRooms,
    locationRequired,
    maxNumberOfAttendees,
    locationNotFound
  } = labels;
  const map = useMap();
  const [autoCompleteBound, setAutoCompleteBound] = useState(false);

  const [searchTermErrorMessage, setSearchTermErrorMessage] = useState<string>();
  const { mapExpanded, mapRef, mapState, setMapState } = useMap();
  const analytics = useAnalytics();
  const searchParams = useSearchParams();
  const { getGeolocationData } = useGeocoder();

  const triggerSearchBarFact = useCallback(() => {
    const eventStartDate = filters?.EVENT_START_DATE ? filters?.EVENT_START_DATE.toISOString() : 'null';
    const eventEndDate = filters?.EVENT_END_DATE ? filters?.EVENT_END_DATE.toISOString() : 'null';

    const checkinDate = checkInDate ? checkInDate.toISOString() : 'null';
    const checkoutDate = checkOutDate ? checkOutDate.toISOString() : 'null';

    const searchBarFact = getSearchBarFact(
      term,
      mapExpanded,
      searchParams.get('p'),
      eventStartDate,
      eventEndDate,
      filters?.NUMBER_OF_SLEEPING_ROOMS?.minimum ?? 'null',
      filters?.NUMBER_OF_ATTENDEES ?? 'null',
      checkinDate,
      checkoutDate
    );
    analytics.reportFact<SearchBarFact>(searchBarFact);
  }, [analytics, filters, searchParams, mapExpanded, term, checkInDate, checkOutDate]);

  // saving country override cookie.. this is being consumed at home page
  useEffect(() => {
    const time = new Date();
    time.setTime(time.getTime() + Number(DECADE_COOKIE_TTL));
    document.cookie = `${COUNTRY_OVERRIDE_COOKIE}=${countryCode};expires=${time};path=/`;
  }, [cookieDomain, countryCode]);

  // remove autocomplete popper on scroll
  useEffect(() => {
    const handleScroll = () => autoComplete?.classList.add('scrolled');
    const autoComplete = document.body.querySelector('.pac-container');
    autoComplete?.classList.remove('scrolled');

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [term]);

  // onChange methods
  const onSearchTermChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (autosuggestion !== null) {
        setAutoSuggestion(null);
      }
      const searchedTerm = e.target.value;
      setTerm(searchedTerm);

      if (!isValidSearchTerm) {
        setIsValidSearchTerm(true);
        setSearchTermErrorMessage('');
      }
      if (searchedTerm.length === 0) {
        setIsValidSearchTerm(false);
        setSearchTermErrorMessage(locationRequired);
      }

      // bind autocomplete once if not done already
      if (searchedTerm.trim().length >= minLength - 1 && !autoCompleteBound) {
        const { googleApiKey, bingApiKey, bingOrGoogle } = map;
        const autocompleteInput = {
          bingOrGoogle,
          searchTermInputRef: inputRefs.searchTermInputRef,
          setTerm,
          setAutoSuggestion,
          locale: locale as unknown as string,
          lat,
          lon,
          radius,
          googleApiKey,
          bingApiKey
        };

        import('@/utils/autocompleteHelper').then(module => {
          module.default(autocompleteInput);
          setAutoCompleteBound(true);
        });
      }
    },
    [
      autosuggestion,
      isValidSearchTerm,
      minLength,
      autoCompleteBound,
      locationRequired,
      map,
      inputRefs.searchTermInputRef,
      locale,
      lat,
      lon,
      radius
    ]
  );

  // check for valid stuff on submission
  const isFormValid = useCallback((): boolean => {
    // validate search term
    if (term.trim().length === 0) {
      setIsValidSearchTerm(false);
      setSearchTermErrorMessage(locationRequired);
      return false;
    }

    // validate attendees
    return validateAttendees(filters.NUMBER_OF_ATTENDEES);
  }, [filters.NUMBER_OF_ATTENDEES, term, locationRequired]);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();
      if (!isFormValid()) {
        return;
      }

      if (filters.SUBREGIONS && term !== searchTerm) {
        const { SUBREGIONS: _, ...rest } = filters;
        setFilters(rest);
      }

      // City and Country filters are reset whenever search term is changed
      // because these filters are dependent on lat,lng.
      if (term !== searchTerm) {
        const { CITY: _, COUNTRY_CODE: __, ...rest } = filters;
        setFilters(rest);
      }
      let filtersWithTimezone = filters;
      // if dates -> append client's timezone
      if (
        filters?.EVENT_START_DATE ||
        filters?.EVENT_END_DATE ||
        filters?.GUEST_CHECKIN_DATE ||
        filters?.GUEST_CHECKOUT_DATE
      ) {
        filtersWithTimezone = { ...filters, TIMEZONE: Intl.DateTimeFormat().resolvedOptions().timeZone };
      }

      let hasError = false;

      // Below logic is for research by clicking on search icon after user have moved to location via map
      let removeExistingGeoParam = false;
      let geoQueryTerm = queryStringConstants.GEO;
      if (searchParams.has(queryStringConstants.LAT) && searchParams.has(queryStringConstants.LNG)) {
        geoQueryTerm = queryStringConstants.PREV_GEO;
        removeExistingGeoParam = true;
      }
      let geocode = searchParams.get(geoQueryTerm) || undefined;
      // if term changed -> call query parser else no need
      if (term !== searchTerm) {
        const result = await getGeolocationData(term, autosuggestion, undefined, isFullMapView(searchParams));
        hasError = result.hasError;
        geocode = result.geocode;
      } else if (removeExistingGeoParam && mapState.center) {
        setMapState(prevState => ({ ...prevState, stopChangePropagation: true }));
        mapRef.current?.panTo(mapState.center);
      }

      if (hasError) {
        setIsValidSearchTerm(false);
        setSearchTermErrorMessage(locationNotFound);
        return;
      }

      setIsSearchExpanded(false);

      // Reason: Google autocomplete script keeps adding container on each query which needs to be removed on subsequent queries
      document.body.querySelector('.pac-container')?.remove();
      const updatedSearchId = refreshSearchCookie(cookieDomain);
      analytics.updateSearchId({ searchId: updatedSearchId });
      triggerSearchBarFact();
      await applyFilters(
        term,
        filtersWithTimezone,
        false,
        term !== searchTerm ? 'CVENT_PICKS' : undefined,
        geocode,
        removeExistingGeoParam
      );
    },
    [
      setIsSearchExpanded,
      isFormValid,
      filters,
      term,
      searchTerm,
      searchParams,
      mapState.center,
      cookieDomain,
      analytics,
      triggerSearchBarFact,
      applyFilters,
      setFilters,
      getGeolocationData,
      autosuggestion,
      setMapState,
      mapRef,
      locationNotFound
    ]
  );

  const searchBarFilters: SearchBarFilter[] = useMemo(
    () => [
      {
        label: search,
        icon: 'map-pin',
        className: 'rounded-l-full truncate',
        inputType: 'text',
        id: 'SEARCH_TERM',
        inputValue: term || '',
        isRequired: true,
        errorMessage: searchTermErrorMessage,
        showErrorMessage: !isValidSearchTerm,
        onChange: onSearchTermChange,
        ref: inputRefs.searchTermInputRef,
        onFocus: () => {
          setIsAutocompleteOpen(true);
        },
        onBlur: () => {
          setIsAutocompleteOpen(false);
        }
      },
      {
        label: startDate,
        icon: 'calendar',
        className: 'cursor-pointer',
        inputType: 'text',
        id: 'EVENT_START_DATE',
        inputValue: eventStart || '',
        onChange: e => {
          e.stopPropagation();
        },
        onPaste: e => {
          e.stopPropagation();
        },
        onKeyDown: e => {
          e.stopPropagation();
        }
      },
      {
        label: endDate,
        icon: 'calendar',
        className: 'cursor-pointer',
        inputType: 'text',
        id: 'EVENT_END_DATE',
        inputValue: eventEnd || '',
        onChange: e => {
          e.stopPropagation();
        },
        onPaste: e => {
          e.stopPropagation();
        },
        onKeyDown: e => {
          e.stopPropagation();
        }
      },
      {
        label: attendees,
        icon: 'users',
        className: 'truncate',
        inputType: 'number',
        id: 'NUMBER_OF_ATTENDEES',
        inputValue:
          filters.NUMBER_OF_ATTENDEES && filters.NUMBER_OF_ATTENDEES > 0 ? filters.NUMBER_OF_ATTENDEES.toString() : '',
        errorMessage: maxNumberOfAttendees,
        showErrorMessage: !validateAttendees(filters.NUMBER_OF_ATTENDEES),
        onChange: onAttendeesChange,
        onPaste,
        onKeyDown: e => {
          allowNumbersOnly(e);
        },
        ref: inputRefs.attendeesInputRef
      },
      {
        label: guestRooms,
        icon: 'bed',
        className: 'truncate',
        inputType: 'number',
        id: 'NUMBER_OF_SLEEPING_ROOMS',
        inputValue: filters.NUMBER_OF_SLEEPING_ROOMS?.minimum
          ? filters.NUMBER_OF_SLEEPING_ROOMS.minimum.toString()
          : '',
        maxRange: MAX_GUEST_ROOMS_LIMIT,
        onChange: onRoomsChange,
        guestRoomDetails: {
          checkInDate,
          checkOutDate,
          onGuestRoomDateChange
        },
        onPaste,
        onKeyDown: e => {
          allowNumbersOnly(e);
        }
      }
    ],
    [
      search,
      term,
      searchTermErrorMessage,
      isValidSearchTerm,
      onSearchTermChange,
      inputRefs.searchTermInputRef,
      inputRefs.attendeesInputRef,
      startDate,
      eventStart,
      endDate,
      eventEnd,
      attendees,
      filters.NUMBER_OF_ATTENDEES,
      filters.NUMBER_OF_SLEEPING_ROOMS?.minimum,
      maxNumberOfAttendees,
      onAttendeesChange,
      guestRooms,
      onRoomsChange,
      checkInDate,
      checkOutDate,
      onGuestRoomDateChange,
      setIsAutocompleteOpen
    ]
  );

  return (
    <div className="w-full">
      <form onSubmit={onSubmit} autoComplete="off">
        <SearchBar
          isGuestRoomDatePickerOpen={isGuestRoomDatePickerOpen}
          setIsGuestRoomDatePickerOpen={setIsGuestRoomDatePickerOpen}
          isDatePickerOpen={isDatePickerOpen}
          setIsDatePickerOpen={setIsDatePickerOpen}
          inputRefs={inputRefs}
          searchBarFilters={searchBarFilters}
          labels={labels}
          selectedRange={{ from: filters?.EVENT_START_DATE, to: filters?.EVENT_END_DATE }}
          handleRangeSelect={handleRangeSelect}
          locale={dateFnsLocale}
          submitHandler={onSubmit}
          setUserModifiedGRDate={setUserModifiedGRDate}
        />
      </form>
    </div>
  );
}
